import { useMenu } from '@if/mui'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import AnnouncementIcon from '@mui/icons-material/Announcement'
import AssessmentIcon from '@mui/icons-material/Assessment'
import ContactsIcon from '@mui/icons-material/Contacts'
import DatasetIcon from '@mui/icons-material/Dataset'
import EventIcon from '@mui/icons-material/Event'
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial'
import HistoryIcon from '@mui/icons-material/History'
import BlockIcon from '@mui/icons-material/Block'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import MergeIcon from '@mui/icons-material/Merge'
import MoneyOffIcon from '@mui/icons-material/MoneyOff'
import NotesIcon from '@mui/icons-material/Notes'
import PermMediaIcon from '@mui/icons-material/PermMedia'
import PersonIcon from '@mui/icons-material/Person'
import PrintIcon from '@mui/icons-material/Print'
import ReplayIcon from '@mui/icons-material/Replay'
import SecurityIcon from '@mui/icons-material/Security'
import SettingsIcon from '@mui/icons-material/Settings'
import SummarizeIcon from '@mui/icons-material/Summarize'
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Tooltip } from '@mui/material'
import { useFeatureFlags } from '@src/api/app-config-hooks'
import { useGenerateValidationReport } from '@src/api/loan/loan-api'
import { useAlertBanner } from '@src/contexts/alert-banner-context'
import { useLoanLock } from '@src/providers/LoanLockProvider'
import { useNotification } from '@src/providers/NotificationProvider'
import { useUser } from '@src/providers/UserProvider'
import { useMonolithBaseUrl } from '@src/services/monolith-hook'
import { ALERT_BANNER_HEIGHT_PX, APP_HEADER_HEIGHT_PX, REPLAY_MIGRATION_COUNTDOWN_SECONDS } from '@src/types/constants'
import { CalendarSimulation } from '@src/types/loan/loan-simulation'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLoanCalendarCurrentStatus } from './LoanCalendar-hooks'
import LoanCalendarStatusSelect from './LoanCalendarStatusSelect'
import ReplayMigrationEventsSnackbar from './ReplayMigrationEventsSnackbar'

const REPLAY_SNACKBAR_KEY = 'replay-migration-events'

const FileMenu = () => {
  const { t } = useTranslation()
  const [buttonProps, menuProps, handleMenuClose] = useMenu()

  return (
    <>
      <Tooltip title={t('loan.payment.fileMenu.tooltip')}>
        <IconButton {...buttonProps}>
          <PersonIcon />
        </IconButton>
      </Tooltip>
      <Menu {...menuProps}>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <MergeIcon />
          </ListItemIcon>
          <ListItemText>{t('loan.payment.fileMenu.fusion')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <ContactsIcon />
          </ListItemIcon>
          <ListItemText>{t('loan.payment.fileMenu.info')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <DatasetIcon />
          </ListItemIcon>
          <ListItemText>{t('loan.payment.fileMenu.data')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <NotesIcon />
          </ListItemIcon>
          <ListItemText>{t('loan.payment.fileMenu.notes')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <PermMediaIcon />
          </ListItemIcon>
          <ListItemText>{t('loan.payment.fileMenu.files')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText>{t('loan.payment.fileMenu.configPPA')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <HistoryIcon />
          </ListItemIcon>
          <ListItemText>{t('loan.payment.fileMenu.history')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose} disabled>
          <ListItemIcon>
            <AccountBalanceIcon />
          </ListItemIcon>
          <ListItemText>{t('loan.payment.fileMenu.metro2')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <AssessmentIcon />
          </ListItemIcon>
          <ListItemText>{t('loan.payment.fileMenu.transactions')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  )
}
interface LoanCalendarActionsProps {
  simulation: CalendarSimulation
}
const LoanCalendarActions = ({ simulation }: LoanCalendarActionsProps) => {
  const { t } = useTranslation()
  const currentStatus = useLoanCalendarCurrentStatus(simulation)
  const user = useUser()
  const { showSuccess, showError } = useNotification()
  const { enqueueSnackbar } = useSnackbar()
  const { banner } = useAlertBanner()
  const { lock, isLocked, isLockedByOther, isLockedBySystem, isLoading, acquireLock, releaseLock } = useLoanLock()

  const canForceUnlock = user?.rights.canForceUnlockLoan ?? false
  const canExecuteDevTools = user?.rights.canExecuteDevTools ?? false
  const featureFlags = useFeatureFlags()
  const loanId = simulation.fullLoan.loan.id
  const monolithBaseUrl = useMonolithBaseUrl()
  const [isReplayPending, setIsReplayPending] = useState(false)
  const generateReportMutation = useGenerateValidationReport(loanId)

  const handleGenerateReportClick = () => {
    if (generateReportMutation.isPending) return

    generateReportMutation.mutate(undefined, {
      onSuccess: () => showSuccess(t('loan.payment.generateReport.success', { email: user?.email })),
      onError: () => showError(t('loan.payment.generateReport.error')),
    })
  }

  const handleReplayMigrationEventsClick = () => {
    if (isReplayPending) return

    const topOffsetPx = APP_HEADER_HEIGHT_PX + (banner.message ? ALERT_BANNER_HEIGHT_PX : 0)
    setIsReplayPending(true)

    enqueueSnackbar(t('loan.payment.replayMigrationEvents.pending', { count: REPLAY_MIGRATION_COUNTDOWN_SECONDS }), {
      key: REPLAY_SNACKBAR_KEY,
      preventDuplicate: true,
      persist: true,
      anchorOrigin: { horizontal: 'center', vertical: 'top' },
      content: (key) => (
        <ReplayMigrationEventsSnackbar
          snackbarKey={key}
          loanId={loanId}
          topOffsetPx={topOffsetPx}
          onClose={() => setIsReplayPending(false)}
        />
      ),
    })
  }

  const handleLockClick = () => {
    if (isLockedBySystem) return
    if (isLocked) {
      void releaseLock()
    } else if (isLockedByOther) {
      if (canForceUnlock) {
        void releaseLock()
      } else {
        const expiresOn = lock?.expiresOn ? new Date(lock.expiresOn).toLocaleString() : 'unknown'
        showError(
          t('common.lockError.failedRelease', {
            username: lock?.username || 'Unknown user',
            expiresOn,
          }),
        )
      }
    } else {
      void acquireLock()
    }
  }

  const getLockTooltip = () => {
    if (isLockedBySystem) {
      return t('common.lockTooltip.lockedBySystem')
    }
    if (isLockedByOther) {
      return (
        <>
          {t('common.lockTooltip.lockedByOther')}
          {canForceUnlock && (
            <>
              <br />
              {t('common.lockTooltip.lockedByOtherAdmin')}
            </>
          )}
        </>
      )
    }
    if (isLocked) {
      return t('common.lockTooltip.locked')
    }
    return t('common.lockTooltip.unlocked')
  }

  const getLockColor = () => {
    if (isLockedBySystem) {
      return 'error'
    }
    if (canForceUnlock && isLockedByOther) {
      return 'warning'
    }
    if (isLockedByOther) {
      return 'error'
    }
    if (isLocked) {
      return 'success'
    }
    return 'default'
  }

  const getLockIcon = () => {
    if (isLockedBySystem) {
      return <BlockIcon />
    }
    if (isLocked || isLockedByOther) {
      return <LockOutlinedIcon />
    }
    return <LockOpenIcon />
  }

  return (
    <Stack sx={{ flexDirection: { xs: 'column-reverse', md: 'row' } }} gap={1}>
      <LoanCalendarStatusSelect currentStatus={currentStatus} />
      <Stack direction="row" alignItems="center">
        <Tooltip title={t('loan.payment.history')}>
          <IconButton>
            <HistoryIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={getLockTooltip()}>
          <span>
            <IconButton onClick={handleLockClick} disabled={isLoading} color={getLockColor()}>
              {getLockIcon()}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('loan.payment.noAlert')}>
          <span>
            <IconButton disabled>
              <AnnouncementIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('loan.payment.print')}>
          <IconButton>
            <PrintIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('loan.payment.noAssurance')}>
          <span>
            <IconButton disabled>
              <SecurityIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('loan.payment.noCollection')}>
          <span>
            <IconButton disabled>
              <MoneyOffIcon />
            </IconButton>
          </span>
        </Tooltip>
        <FileMenu />
        <Tooltip title={t('loan.payment.subscriptionFile')}>
          <IconButton>
            <FolderSpecialIcon />
          </IconButton>
        </Tooltip>
        {canExecuteDevTools && (
          <Tooltip title={t('loan.payment.replayMigrationEvents.tooltip')}>
            <span>
              <IconButton onClick={handleReplayMigrationEventsClick} disabled={isReplayPending}>
                <ReplayIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {!featureFlags.shutdownMonolith && (
          <Tooltip title={t('loan.payment.openMonolithCalendar')}>
            <IconButton
              href={`${monolithBaseUrl}/admin/customer/${simulation.fullLoan.loan.originalLoanId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <EventIcon />
            </IconButton>
          </Tooltip>
        )}
        {!featureFlags.shutdownMonolith && canExecuteDevTools && (
          <Tooltip title={t('loan.payment.generateReport.tooltip')}>
            <span>
              <IconButton onClick={handleGenerateReportClick} disabled={generateReportMutation.isPending}>
                <SummarizeIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  )
}

export default LoanCalendarActions
